import { Link } from 'react-router-dom';
import { DocSection } from '../../../pages/shell';
import { DocShot } from '../../DocShot';
import { IconTile, type IconName } from '../../../components/icons';

export const toc = [
  { id: 'how-it-works', label: 'How ClipRadar works' },
  { id: 'get-started', label: 'Get started' },
  { id: 'go-deeper', label: 'Go deeper' },
  { id: 'for-agents', label: 'For AI-agent users' },
];

// Small info card — value-prop highlight, links out to the page that owns the fact.
function InfoCard({ icon, title, text, to, cta }: { icon: IconName; title: string; text: string; to: string; cta: string }) {
  return (
    <Link
      to={to}
      className="crw-card-hover crw-glass"
      style={{
        textDecoration: 'none', borderBottom: 'none', display: 'flex', flexDirection: 'column', gap: 10,
        padding: '18px 18px 16px', borderRadius: 14,
      }}
    >
      <IconTile d={icon} />
      <div>
        <b style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: 'var(--fg-1)' }}>{title}</b>
        <p style={{ margin: '5px 0 0', fontSize: 13, lineHeight: 1.55, color: 'var(--fg-3)' }}>{text}</p>
      </div>
      <span className="crw-doc-cta" style={{ marginTop: 'auto', paddingTop: 4, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--signal-400)' }}>{cta}</span>
    </Link>
  );
}

// Door card — the primary route-by-intent CTA for a whole section.
function DoorCard({ to, title, text, cta }: { to: string; title: string; text: string; cta: string }) {
  return (
    <div style={{ marginTop: 8, padding: '20px 22px', borderRadius: 14, border: '1px solid var(--line-2)', background: 'var(--surface-card)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--fg-1)' }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--fg-3)', marginTop: 3 }}>{text}</div>
      </div>
      <Link to={to} className="crw-doc-cta" style={{ flex: 'none', fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--signal-400)', textDecoration: 'none', borderBottom: 'none', whiteSpace: 'nowrap' }}>{cta}</Link>
    </div>
  );
}

export default function Body() {
  return (
    <>
      <DocSection id="how-it-works" title="How ClipRadar works">
        <p>
          ClipRadar finds moments in your footage by what&rsquo;s on screen, what&rsquo;s said, or who&rsquo;s in
          frame — instead of you scrubbing a timeline. Analyze your library once, then search it the way
          you&rsquo;d describe a shot to a colleague.
        </p>
        <DocShot
          slug="get-started"
          shot="search-hero"
          alt="ClipRadar Search tab showing results for a natural-language query"
          caption="The Search tab: type what you remember, get back the matching scenes."
        />
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginTop: 18 }}
        >
          <InfoCard
            icon="search"
            title="Search what you remember"
            text="Describe what's on screen, quote what someone said, or name who's in it. Visual, Speech, and Ultra Search cover every signal — visual results are deduped to one per scene."
            to="/docs/concepts/how-it-works"
            cta="How search works →"
          />
          <InfoCard
            icon="cpu"
            title="Local, on your GPU"
            text="Every analysis pass runs on your own NVIDIA GPU, and every search runs locally — footage never uploads anywhere."
            to="/docs/concepts/local-and-private"
            cta="Local & private →"
          />
          <InfoCard
            icon="film"
            title="Made for editors"
            text="Results land trimmed on your timeline — in the desktop app, or right inside the Premiere Pro panel."
            to="/docs/platforms/premiere-panel"
            cta="The Premiere panel →"
          />
        </div>
      </DocSection>

      <DocSection id="get-started" title="Get started">
        <p>New to ClipRadar? Go from download to your first search in a handful of steps.</p>
        <DoorCard
          to="/docs/quickstart"
          title="Quickstart"
          text="Install, add footage, analyze, activate, and run your first search — six steps, start to finish."
          cta="Start here →"
        />
      </DocSection>

      <DocSection id="go-deeper" title="Go deeper">
        <p>Once you&rsquo;re searching, learn every tab so nothing in the interface catches you off guard.</p>
        <DoorCard
          to="/docs/interface"
          title="The ClipRadar interface"
          text="Search, Media, People, Settings, and Help — what each tab does and every control on it."
          cta="Learn the interface →"
        />
      </DocSection>

      <DocSection id="for-agents" title="For AI-agent users">
        <p>
          Prefer to drive ClipRadar from an AI coding agent instead of clicking through the UI? ClipRadar ships
          a local <strong>Agent (MCP)</strong> server — it runs locally, so your footage never uploads. The
          agent works with your library&rsquo;s metadata, transcripts, and thumbnails — never the raw video.
        </p>
        <DoorCard
          to="/docs/concepts/agentic-editing"
          title="Agentic editing (MCP)"
          text="Connect Claude Code, Claude Desktop, or Codex and let it search, verify, and build timelines for you."
          cta="Connect an agent →"
        />
      </DocSection>
    </>
  );
}
